import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';

/**
 * NoticePopup - Professionelles Hinweis-Pop-up
 * 
 * Dieses Pop-up wird beim ersten Besuch der Website angezeigt.
 * Nach dem Schließen wird es für denselben Besucher nicht mehr angezeigt (localStorage).
 * 
 * Um das Pop-up zu deaktivieren:
 * - Setze POPUP_ENABLED auf false, oder
 * - Entferne die <NoticePopup /> Komponente aus App.js
 */

// ============================================
// KONFIGURATION - Hier können Sie das Pop-up steuern
// ============================================
const POPUP_ENABLED = true; // Auf false setzen, um das Pop-up zu deaktivieren
const STORAGE_KEY = 'sunprojects_notice_dismissed';
// ============================================

const NoticePopup = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Prüfe ob Pop-up aktiviert ist und ob es bereits geschlossen wurde
    if (!POPUP_ENABLED) return;
    
    const isDismissed = localStorage.getItem(STORAGE_KEY);
    if (!isDismissed) {
      // Kurze Verzögerung für bessere UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="notice-title"
    >
      {/* Backdrop - Abdunklung */}
      <div 
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Pop-up Container */}
      <div 
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300"
        data-testid="notice-popup"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-4 flex items-center justify-between">
          <h2 
            id="notice-title"
            className="text-lg font-semibold text-white"
          >
            Hinweis in eigener Sache
          </h2>
          <button
            onClick={handleClose}
            className="text-white/70 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            aria-label="Schließen"
            data-testid="notice-popup-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-6 py-6">
          <div className="space-y-4 text-slate-700 text-[15px] leading-relaxed">
            <p>
              Auf unserer Website wurde ein extern eingebundenes Video ohne unser Wissen 
              durch einen Dritten ausgetauscht und zeigte dadurch vorübergehend 
              unangemessene Inhalte.
            </p>
            <p>
              Das Video wurde umgehend entfernt. Wir entschuldigen uns bei allen 
              Besucherinnen und Besuchern, die sich durch die Darstellung irritiert 
              oder angegriffen gefühlt haben.
            </p>
            <p>
              Dieser Inhalt stammte ausdrücklich nicht von uns und entspricht 
              selbstverständlich nicht unserer Kommunikation gegenüber Kunden 
              und Geschäftspartnern.
            </p>
            <p className="font-medium text-slate-800">
              Vielen Dank für Ihr Verständnis.
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
          <Button
            onClick={handleClose}
            className="bg-sun-orange hover:bg-sun-orange-dark text-white px-8 py-2.5 font-semibold shadow-lg shadow-sun-orange/20 hover:shadow-sun-orange/30 transition-all"
            data-testid="notice-popup-confirm"
          >
            Verstanden
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NoticePopup;
